import React, { useEffect, useState } from "react";
import { Trophy, BarChart3 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * LastWeekResultsBanner — slim recap shown on the Landing page for the
 * first 24 hours of each new poll week. Surfaces the previous week's
 * winning answer + the full distribution so visitors see how the
 * camping community voted before they move on to this week's poll.
 *
 *   • Backend (`/api/polls/last-week`) returns `available: false` when
 *     either zero votes were cast last week OR we're outside the 24-hour
 *     reveal window — the banner self-hides in both cases.
 *   • Dismissable per-week via localStorage so the same camper isn't
 *     re-shown the recap on every revisit.
 */
export default function LastWeekResultsBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/polls/last-week").then(({ data: r }) => {
      if (!alive) return;
      if (!r || !r.available) return;
      // Per-week dismissal — key on the poll_id+week_index combo.
      const key = `cc_lastweek_seen_${r.poll_id}_${r.week_index}`;
      if (typeof window !== "undefined" && window.localStorage?.getItem(key)) {
        setDismissed(true);
        return;
      }
      setData(r);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data || dismissed) return null;

  const winner = (data.options || []).reduce(
    (best, o) => (o.count > (best?.count ?? -1) ? o : best),
    null,
  );
  const sorted = [...(data.options || [])].sort((a, b) => b.count - a.count);

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        `cc_lastweek_seen_${data.poll_id}_${data.week_index}`,
        "1",
      );
    } catch {/* localStorage may be blocked */}
    setDismissed(true);
  };

  return (
    <section
      className="border-b hairline bg-[#fef9c3]"
      data-testid="last-week-results-banner"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <BarChart3 className="w-4 h-4 text-[#7c2d12]" />
              <span
                className="mono text-[10px] uppercase tracking-[0.3em] text-[#7c2d12]"
                data-testid="last-week-eyebrow"
              >
                Last week&apos;s poll · results
              </span>
            </div>
            <h3
              className="font-display text-xl sm:text-2xl text-[#1c1917] leading-tight"
              data-testid="last-week-question"
            >
              {data.question}
            </h3>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-[#7c2d12] hover:text-[#1c1917] mono text-[10px] uppercase tracking-[0.2em] underline underline-offset-2 shrink-0"
            data-testid="last-week-dismiss"
          >
            Hide
          </button>
        </div>

        {winner && winner.count > 0 ? (
          <div
            className="flex items-center gap-2 mb-4 text-[#1c1917]"
            data-testid="last-week-winner"
          >
            <Trophy className="w-4 h-4 text-[#b45309]" />
            <span className="text-sm">
              <span className="font-bold">{winner.label}</span> won with{" "}
              <span className="font-bold">{winner.pct}%</span> of {data.total} vote{data.total === 1 ? "" : "s"}
            </span>
          </div>
        ) : null}

        <ol
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          data-testid="last-week-results-list"
        >
          {sorted.map((o, i) => (
            <li
              key={o.key}
              className="relative bg-white border hairline px-3 py-2 overflow-hidden"
            >
              <div
                className="absolute inset-y-0 left-0 bg-[#fde68a]"
                style={{ width: `${o.pct}%` }}
                aria-hidden="true"
              />
              <div className="relative flex items-center justify-between gap-3 text-sm">
                <span className="text-[#1c1917]">
                  <span className="mono text-[11px] text-[#a3a3a3] mr-1.5">{i + 1}.</span>
                  {o.label}
                </span>
                <span className="mono text-[12px] text-[#1c1917] tabular-nums">
                  {o.pct}% · {o.count}
                </span>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-3 text-[12px] text-[#7c2d12] mono">
          This week&apos;s poll is live below — cast your vote.
        </p>
      </div>
    </section>
  );
}
